import { useEffect, useState } from "react";
import { Shield, X, RotateCcw, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { populateDefaults, populateDemoData } from "@/utils/demo-data";
import { toast } from "@/hooks/use-toast";

export function ImpersonationBanner() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [targetName, setTargetName] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    const active = sessionStorage.getItem("impersonation_active");
    if (active === "true") {
      setIsImpersonating(true);
      setTargetName(sessionStorage.getItem("impersonation_target_name") || "");
      setTargetEmail(sessionStorage.getItem("impersonation_target_email") || "");
    }
  }, []);

  // Check if the impersonated user is in demo mode
  useEffect(() => {
    if (!isImpersonating || !user) return;
    const check = async () => {
      const { data } = await supabase
        .from("user_preferences")
        .select("has_demo_data")
        .eq("user_id", user.id)
        .maybeSingle();
      setIsDemoMode((data as any)?.has_demo_data === true);
    };
    check();
  }, [isImpersonating, user]);

  const handleEndSession = async () => {
    sessionStorage.removeItem("impersonation_active");
    sessionStorage.removeItem("impersonation_target_name");
    sessionStorage.removeItem("impersonation_target_email");
    await supabase.auth.signOut();
    window.close();
  };

  const handleResetToDefaults = async () => {
    if (!user || isResetting) return;
    setIsResetting(true);

    try {
      // 1. Delete ALL contract-related data (FK order)
      // contract_notes
      await (supabase.from("contract_notes").delete() as any).eq("user_id", user.id);
      
      // contract_contacts
      const { data: allContracts } = await supabase
        .from("contracts")
        .select("id")
        .eq("user_id", user.id);
      if (allContracts && allContracts.length > 0) {
        await supabase.from("contract_contacts").delete().in("contract_id", allContracts.map(c => c.id));
      }

      // contracts
      await supabase.from("contracts").delete().eq("user_id", user.id);

      // contact_persons
      await supabase.from("contact_persons").delete().eq("user_id", user.id);

      // suppliers
      await supabase.from("suppliers").delete().eq("user_id", user.id);

      // exchange_rates (need to delete before currencies)
      const { data: currencies } = await supabase
        .from("currencies")
        .select("id")
        .eq("user_id", user.id);
      if (currencies && currencies.length > 0) {
        const currencyIds = currencies.map(c => c.id);
        await supabase.from("exchange_rates").delete().or(
          currencyIds.map(id => `base_currency_id.eq.${id}`).join(",")
        );
      }

      // categories
      await supabase.from("categories").delete().eq("user_id", user.id);

      // contract_types
      await supabase.from("contract_types").delete().eq("user_id", user.id);

      // currencies
      await supabase.from("currencies").delete().eq("user_id", user.id);

      // 2. Re-populate defaults and demo data
      await populateDefaults(user.id);
      await populateDemoData(user.id);

      // 3. Ensure has_demo_data is true
      await supabase
        .from("user_preferences")
        .update({ has_demo_data: true } as any)
        .eq("user_id", user.id);

      // 4. Invalidate caches
      await queryClient.invalidateQueries();
      setIsDemoMode(true);

      toast({
        title: t("common:admin.impersonate.resetSuccess"),
        description: t("common:admin.impersonate.resetSuccessDesc"),
      });
    } catch (error) {
      console.error("Error resetting user data:", error);
      toast({
        title: t("common:admin.impersonate.resetError"),
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  if (!isImpersonating) return null;

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4" />
          <span className="text-sm font-medium">
            {t("common:admin.impersonate.bannerText", { 
              name: targetName, 
              email: targetEmail 
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isDemoMode && (
            <button
              onClick={handleResetToDefaults}
              disabled={isResetting}
              className="flex items-center gap-1 text-sm font-medium bg-amber-800 hover:bg-amber-900 text-white px-3 py-1 rounded disabled:opacity-50"
            >
              {isResetting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RotateCcw className="h-3 w-3" />
              )}
              {t("common:admin.impersonate.resetDefaults")}
            </button>
          )}
          <button
            onClick={handleEndSession}
            className="flex items-center gap-1 text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded"
          >
            <X className="h-3 w-3" />
            {t("common:admin.impersonate.endSession")}
          </button>
        </div>
      </div>
      {/* Spacer to push content below the fixed banner */}
      <div className="h-10 w-full" />
    </>
  );
}
